

enum InvitationsStatus {
  NoInvitations = 'noInvitations',
  AllAccepted = 'allAccepted',
  AllSent = 'allSent',
  AllRevoked = 'allRevoked',
  AllExpired = 'allExpired',
  HasFailures = 'hasFailures',
  Pending = 'pending',
}

import RideitInvitation, { RideitInvitationStatus } from '@/models/Rideit/RideitInvitation';
import SmsMessage from '@/models/SmsMessage';
import { Component, Prop, Vue } from "vue-property-decorator";
import { IInvitationWithSms } from './RideitInvitationCard.vue';
import RideitInvitationsDialog from './RideitInvitationsDialog.vue';
import { getInvitationStatus, getLatestInvitationPerPhoneNumber, RideitInvitationWithSmsStatus } from '@/util/rideitHelper';
import moment from 'moment';


@Component({
  components: {
    RideitInvitationsDialog,
  },
})
export default class RideitInvitationsStatus extends Vue {
  @Prop({ default: []}) private invitations: RideitInvitation[];
  @Prop({ default: []}) private smsMessages: SmsMessage[];
  @Prop({ required: true}) private passengerName: string;

  invitationsDialog = false;

  get invitationItems() {
    return getLatestInvitationPerPhoneNumber(this.invitations, this.smsMessages);
  }

  get invitationItemStatuses() {
    return this.invitationItems.map(item => getInvitationStatus(item.invitation, item.smsMessage));
  }

  get invitationsStatus(): InvitationsStatus {
    if (this.invitations.length === 0) {
      return InvitationsStatus.NoInvitations;
    }
    if (this.invitationItemStatuses.every(status => status === RideitInvitationWithSmsStatus.Accepted)) {
      return InvitationsStatus.AllAccepted;
    }
    if (this.invitationItemStatuses.every(status => status === RideitInvitationWithSmsStatus.Revoked)) {
      return InvitationsStatus.AllRevoked;
    }
    if (this.invitationItemStatuses.every(status => status === RideitInvitationWithSmsStatus.Expired)) {
      return InvitationsStatus.AllExpired;
    }
    if (this.invitationItemStatuses.every(status => status === RideitInvitationWithSmsStatus.Sent)) {
      return InvitationsStatus.AllSent;
    }
    if (this.invitationItemStatuses.some(status => status === RideitInvitationWithSmsStatus.Failed)) {
      return InvitationsStatus.HasFailures;
    }
    return InvitationsStatus.Pending;
  }

  get allowShowingDetails() {
    return this.invitationsStatus === InvitationsStatus.HasFailures;
  }

  get color() {
    switch (this.invitationsStatus) {
      case InvitationsStatus.NoInvitations:
        return '';
      case InvitationsStatus.AllAccepted:
        return 'success';
      case InvitationsStatus.AllSent:
        return 'success';
      case InvitationsStatus.Pending:
        return 'warning';
      case InvitationsStatus.HasFailures:
      case InvitationsStatus.AllRevoked:
      case InvitationsStatus.AllExpired:
        return 'error';
    }
  }

  get textColor() {
    const blackColors = ['warning', 'success'];
    if (blackColors.includes(this.color)) {
      return 'black';
    }
    return '';
  }

  get hasOutline() {
    const outlinedStatuses = [InvitationsStatus.AllSent, InvitationsStatus.Pending];
    return outlinedStatuses.includes(this.invitationsStatus);
  }

  get statusText() {
    switch (this.invitationsStatus) {
      case InvitationsStatus.NoInvitations:
        return this.$t('rideIt.invitations_status.noInvitations');
      case InvitationsStatus.AllAccepted:
        return this.$t('rideIt.invitations_status.allAccepted');
      case InvitationsStatus.AllSent:
        return this.$t('rideIt.invitations_status.allSent');
      case InvitationsStatus.AllRevoked:
        return this.$t('rideIt.invitations_status.allRevoked');
      case InvitationsStatus.AllExpired:
        return this.$t('rideIt.invitations_status.allExpired');
      case InvitationsStatus.Pending:
        const pendingCount = this.invitationItemStatuses.filter(i => i === RideitInvitationWithSmsStatus.Pending).length;
        return this.getRecapString(this.$t('rideIt.invitations_status.pending'), pendingCount, this.invitationItems.length);
        
      case InvitationsStatus.HasFailures:
        const failedCount = this.invitationItemStatuses.filter(i => i === RideitInvitationWithSmsStatus.Failed).length;
        return this.getRecapString(this.$t('rideIt.invitations_status.hasFailures'), failedCount, this.invitationItems.length);
        
    }
  }

  getLatestPerPhone(items: IInvitationWithSms[]) {
    return Array.from(items.reduce((map, item) => {
      // If there are multiple messages to the same number, find the previous map entry for it.
      const existing = map.get(item.smsMessage.to);

      if (!existing) {
        // If we don't have an entry, add it and move on.
        map.set(item.smsMessage.to, item);
        return map;
      }

      // Get times for comparison
      const messageTime = moment(item.smsMessage.statusUpdatedAt);
      const existingMessageTime = moment(existing.smsMessage.statusUpdatedAt);

      if (messageTime.isAfter(existingMessageTime)) {
        // current message is newer, replace the old entry
        map.set(item.smsMessage.to, item);
      }

      return map;
    }, new Map<string, IInvitationWithSms>()).values());
  }

  getRecapString(status: string, amount: number, totalAmount: number) {
    return [ status, this.getAmountRecap(amount, totalAmount) ].filter(Boolean).join(' ');
  }

  getAmountRecap(itemAmount: number, totalAmount: number) {
    if (totalAmount === 1) {
      return '';
    }
    return `${itemAmount}/${totalAmount}`;
  }

  toggleInvitationsDialog() {
    this.invitationsDialog = !this.invitationsDialog;
  }
}
